<template>
    <div :class="$style.container">
        <Image
            ref="imageRef"
            withFadeInOnLoad
            :class="$style.image_override"
            :src="props.src"
            :alt="props.alt"
            @loaded="isImageLoaded = true"
        />
    </div>
</template>
<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { onMounted, onUnmounted, ref } from 'vue'

import Image from '@/components/Image/Image.vue'

const PARALLAX_OPACITY_PX = 300

const props = defineProps<{
    src: string
    alt: string
}>()

const isDesktop = useMediaQuery('(min-width: 960px)')

const imageRef = ref<InstanceType<typeof Image>>()
const isImageLoaded = ref(false)

const handleScrollParallax = () => {
    requestAnimationFrame(() => {
        const imageEl = imageRef.value?.$el
        if (!imageEl) {
            return
        }
        const scrollY = Math.max(window.scrollY, 0)
        // if page already had scroll, disable parallax
        const wasPreScrolled = scrollY > 50 && !imageEl.style.transform
        if (wasPreScrolled) {
            window.removeEventListener('scroll', handleScrollParallax)
            return
        }
        imageEl.style.transform = `translate3d(0px, ${scrollY * 0.3}px, 0px)`
        if (isImageLoaded.value) {
            imageEl.style.opacity = `${1 - Math.min(scrollY, PARALLAX_OPACITY_PX) / PARALLAX_OPACITY_PX}`
        }
    })
}

onMounted(() => {
    if (!isDesktop.value) {
        return
    }
    window.addEventListener('scroll', handleScrollParallax, { passive: true })
})
onUnmounted(() => {
    window.removeEventListener('scroll', handleScrollParallax)
})
</script>

<style module>
.container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 260px;
    overflow: hidden;
}

.container::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(to bottom, transparent 80%, black);
}

.image_override {
    object-fit: cover;
    margin: auto;
    object-position: center;
    max-height: 260px;
    position: absolute;
    top: 0;
    bottom: 0;
    will-change: transform, opacity;
    user-select: none;
}
</style>
